import { useCallback } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { LightbulbIcon } from "@lucide/icons";

import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Card, CardContent, CardDescription } from "simplydo/react/ui/card";
import { H2, H3 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Message } from "simplydo/react/ui/message";

import { useAppSelector } from "store";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";

import InviteeChip from "./InviteeChip";
import type { InviteCardProps } from "./types";

const IdeaInviteCard = ({
  invitation,
  acceptInvite,
  rejectInvite,
  removeInvite,
  cardView = true,
}: InviteCardProps) => {
  const {
    inviterUser,
    inviteeUser,
    invitee,
    challenge,
    forIdeas = [],
    createdByCurrentUser,
  } = invitation;
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const accept = useCallback(() => {
    acceptInvite(
      invitation,
      () => {
        navigate(`/challenges/${challenge?._id}/assessments/assigned`);
        toast.success(`Became ${t("generic.idea")} assessor`);
      },
      () => toast.error(`Error becoming ${t("generic.idea")} assessor`),
    );
  }, [acceptInvite, invitation, challenge, t, navigate]);

  const reject = useCallback(() => {
    util
      .confirm(
        "Reject assessment invitation",
        `Are you sure you want to reject an invitation from ${inviterUser?.profile?.fullName} to assess ${util.pluralise(forIdeas.length, t("generic.idea"), t("generic.ideas"))} in "${challenge?.name || `Deleted ${t("generic.challenge")}`}?`,
      )
      .then(() => {
        rejectInvite(
          invitation,
          () => {
            toast("Invitation rejected");
            navigate(`/`);
          },
          () => toast.error("Error rejecting invite"),
        );
      })
      .catch(() => {});
  }, [
    rejectInvite,
    invitation,
    inviterUser?.profile?.fullName,
    forIdeas,
    challenge?.name,
    t,
    navigate,
  ]);

  const remove = useCallback(() => {
    util
      .confirm(
        "Remove assessment invitation",
        `Are you sure you want to remove this invitation to ${inviteeUser?.profile?.fullName || invitee} to assess ${t("generic.ideas")} in "${challenge?.name || `Deleted ${t("generic.challenge")}`}"?`,
      )
      .then(() => {
        removeInvite(
          invitation,
          () => {
            toast("Invitation removed");
            navigate(`/challenges/${challenge?._id}/assessments`);
          },
          () => toast.error("Error removing invite"),
        );
      })
      .catch(() => {});
  }, [
    removeInvite,
    invitation,
    challenge?.name,
    inviteeUser?.profile?.fullName,
    invitee,
    t,
    navigate,
    challenge?._id,
  ]);

  const canAccept = invitation.canAccept && !!challenge;

  return (
    <div
      style={
        cardView
          ? {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }
          : {}
      }
    >
      <div>
        {cardView ? (
          <H3 style={{ marginBottom: "0.3em" }}>
            <Link to={challenge ? `/challenges/${challenge._id}` : ""}>
              {challenge?.name || `Deleted ${t("generic.challenge")}`}
            </Link>
          </H3>
        ) : (
          <H2 style={{ textAlign: "center" }}>
            <Icon icon={LightbulbIcon} />
            Assess {challenge?.name || `Deleted ${t("generic.challenge")}`}
          </H2>
        )}
        <CardDescription>
          {createdByCurrentUser ? (
            <InviteeChip
              invitation={invitation}
              preText="You invited"
              postText={`to assess ${util.pluralise(forIdeas.length, t("generic.idea"), t("generic.ideas"))} in this ${t("generic.challenge")}`}
            />
          ) : (
            <>
              <UserChip user={inviterUser} compact="very" /> has invited you to
              assess{" "}
              {util.pluralise(
                forIdeas.length,
                t("generic.idea"),
                t("generic.ideas"),
              )}{" "}
              in this {t("generic.challenge")}. Accept this invitation to start
              assessing the{" "}
              {util.pluralise(
                forIdeas.length,
                t("generic.idea"),
                t("generic.ideas"),
                false,
              )}{" "}
              in &quot;
              {challenge?.name || `Deleted ${t("generic.challenge")}`}
              &quot;.
            </>
          )}
        </CardDescription>
      </div>
      <CardContent>
        <div
          style={
            cardView
              ? { display: "flex", justifyContent: "flex-end" }
              : { marginTop: 20, display: "flex", justifyContent: "flex-end" }
          }
        >
          {invitation.acceptedAt && challenge && (
            <ButtonLink
              color="green"
              variant="outline"
              to={`/challenges/${challenge?._id}/assessments/assigned`}
            >
              Go to assessments
            </ButtonLink>
          )}
          {!invitation.acceptedAt && (
            <>
              {!challenge && (
                <Message color="red" style={{ width: "100%" }}>
                  This invitation to assess is no longer valid because the{" "}
                  {t("generic.challenge")} has been deleted.
                </Message>
              )}
              {user && invitation.canRemove ? (
                <Button
                  color={createdByCurrentUser ? undefined : "red"}
                  onClick={createdByCurrentUser ? remove : reject}
                  variant="outline"
                >
                  {createdByCurrentUser ? "Delete invitation" : "Reject"}
                </Button>
              ) : null}
              {createdByCurrentUser && challenge ? (
                <ButtonLink
                  color="primary"
                  to={`/challenges/${challenge._id}/assessments`}
                >
                  Go to assessments
                </ButtonLink>
              ) : null}
              {user && canAccept && !createdByCurrentUser ? (
                <Button color="green" onClick={accept} variant="outline">
                  Accept
                </Button>
              ) : null}
            </>
          )}
        </div>
      </CardContent>
    </div>
  );
};

export default IdeaInviteCard;
